import React, { useState, useEffect, useMemo } from "react";
import { ConfigProvider, Table } from "antd";
import { decryptText } from "../utils/crypto";
import { fetchProducts } from "../services/shopify";
import * as XLSX from 'xlsx';

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [decrypting, setDecrypting] = useState(false);
  const [decryptError, setDecryptError] = useState(null);
  // Removed unused decryptedUrl state
  const [checkingSession, setCheckingSession] = useState(true);

  const ENCRYPTED_URL = process.env.REACT_APP_ENCRYPTED_INVENTORY_URL;

  useEffect(() => {
    const checkSession = async () => {
      const savedPassword = sessionStorage.getItem("inventory_password");
      if (savedPassword && ENCRYPTED_URL) {
        try {
          const decrypted = await decryptText(ENCRYPTED_URL, savedPassword);
          if (decrypted.startsWith("http")) {
            setIsAuthenticated(true);
          } else {
            sessionStorage.removeItem("inventory_password");
          }
        } catch (e) {
          sessionStorage.removeItem("inventory_password");
        }
      }
      setCheckingSession(false);
    };
    checkSession();
  }, [ENCRYPTED_URL]);

  const handleUnlock = async (e) => {
    if (e) e.preventDefault();
    if (!passwordInput.trim()) return;

    setDecrypting(true);
    setDecryptError(null);

    try {
      if (!ENCRYPTED_URL) {
        throw new Error("No encrypted inventory URL configured.");
      }
      const decrypted = await decryptText(ENCRYPTED_URL, passwordInput);
      if (decrypted.startsWith("http")) {
        sessionStorage.setItem("inventory_password", passwordInput);
        setIsAuthenticated(true);
      } else {
        throw new Error("Invalid password");
      }
    } catch (err) {
      setDecryptError("Incorrect password. Access denied.");
    } finally {
      setDecrypting(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      console.log("Not authenticated. Skipping data fetch.");
      return;
    }

    console.log("Starting data fetch from Shopify...");
    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("Fetching products...");
        const products = await fetchProducts();
        if (cancelled) {
          console.log("Fetch cancelled, not updating state.");
          return;
        }
        console.log(`Fetched ${products.length} products.`);
        setInventory(products);
      } catch (err) {
        if (cancelled) {
          console.log("Fetch cancelled during error handling.");
          return;
        }
        console.error("Error fetching products:", err);
        setError(err?.message || "Failed to load inventory from Shopify.");
        setInventory([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          console.log("Finished data fetch.");
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
      console.log("Component unmounted or effect cleaned up. Cancel fetch.");
    };
  }, [isAuthenticated, retryCount]);

  const visibleInventory = useMemo(() => {
    return inventory.filter(
      (item) => item?.listOnWebsite === true && Number(item?.quantity) > 0,
    );
  }, [inventory]);

  const filteredData = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return visibleInventory;

    const result = visibleInventory.filter((item) => {
      const title = String(item?.title || "").toLowerCase();
      const barcode = String(item?.barcode || "").toLowerCase();
      const vendor = String(item?.vendor || "").toLowerCase();

      return (
        title.includes(search) ||
        barcode.includes(search) ||
        vendor.includes(search)
      );
    });
    return result;
  }, [visibleInventory, searchTerm]);

  const brandFilters = useMemo(() => {
    const unique = [
      ...new Set(visibleInventory.map((i) => i["vendor"]).filter(Boolean)),
    ];
    return unique.map((b) => ({ text: b, value: b }));
  }, [visibleInventory]);

  const platformFilters = useMemo(() => {
    const unique = [
      ...new Set(visibleInventory.map((i) => i["platform"]).filter(Boolean)),
    ];
    return unique.map((p) => ({ text: p, value: p }));
  }, [visibleInventory]);

  const ratingFilters = useMemo(() => {
    const unique = [
      ...new Set(visibleInventory.map((i) => i["rating"]).filter(Boolean)),
    ];
    return unique.map((r) => ({ text: `Rating: ${r}`, value: r }));
  }, [visibleInventory]);

  const columns = [
    {
      title: "Barcode",
      key: "barcode",
      width: 160,
      sorter: (a, b) =>
        String(a?.barcode || "").localeCompare(String(b?.barcode || "")),
      render: (_, record) => (
        <span
          style={{
            color: "#ef4444",
            fontWeight: "bold",
            fontFamily: "monospace",
          }}
        >
          {String(record?.barcode || "NO BARCODE")}
        </span>
      ),
    },
    {
      title: "Product Name",
      key: "title",
      width: 360,
      sorter: (a, b) =>
        String(a?.title || "").localeCompare(String(b?.title || "")),
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          {record?.image ? (
            <img
              src={record.image}
              alt={record?.imageAlt || record?.title || "Product"}
              style={{
                width: "48px",
                height: "48px",
                objectFit: "cover",
                borderRadius: "8px",
                backgroundColor: "#f3f4f6",
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "8px",
                backgroundColor: "#f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: "20px",
              }}
            >
              📦
            </div>
          )}
          <div
            style={{
              color: "#111827",
              fontWeight: "bold",
            }}
          >
            {String(record?.title || "NO TITLE")}
          </div>
        </div>
      ),
    },
    {
      title: "Brand",
      key: "vendor",
      width: 180,
      filters: brandFilters,
      onFilter: (value, record) => record?.vendor === value,
      filterSearch: true,
      sorter: (a, b) =>
        String(a?.vendor || "").localeCompare(String(b?.vendor || "")),
      render: (_, record) => (
        <span style={{ color: "#111827" }}>
          {String(record?.vendor || "NO BRAND")}
        </span>
      ),
    },
    {
      title: "Platform",
      key: "platform",
      width: 150,
      filters: platformFilters,
      onFilter: (value, record) => record?.platform === value,
      filterSearch: true,
      render: (_, record) => (
        <span style={{ color: "#6b7280" }}>
          {String(record?.platform || "-")}
        </span>
      ),
    },
    {
      title: "Rating",
      key: "rating",
      width: 120,
      filters: ratingFilters,
      onFilter: (value, record) => record?.rating === value,
      filterSearch: true,
      render: (_, record) => (
        <span style={{ color: "#6b7280" }}>
          {String(record?.rating || "-")}
        </span>
      ),
    },
    {
      title: "Qty",
      key: "quantity",
      width: 90,
      sorter: (a, b) => Number(a?.quantity || 0) - Number(b?.quantity || 0),
      render: (_, record) => (
        <span style={{ color: "#111827", fontWeight: "bold" }}>
          {Number(record?.quantity || 0)}
        </span>
      ),
    },
    {
      title: "SRP",
      key: "price",
      width: 120,
      sorter: (a, b) => Number(a?.price || 0) - Number(b?.price || 0),
      render: (_, record) => {
        const price = Number(record?.price);
        if (!Number.isFinite(price)) {
          return <span style={{ fontWeight: "bold" }}>-</span>;
        }
        return (
          <span
            style={{
              color: "#111827",
              fontWeight: "bold",
            }}
          >
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: record?.currency || "USD",
            }).format(price)}
          </span>
        );
      },
    },
  ];

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory");
    XLSX.writeFile(workbook, "inventory.xlsx");
  };

  if (checkingSession) {
    return (
      <div className="bg-bg-light min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-red mx-auto mb-4"></div>
          <p className="text-text-muted font-semibold">
            Checking Authorization...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="bg-bg-light min-h-[85vh] flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-card border border-border-light p-8 md:p-10 relative overflow-hidden transition-all duration-300 hover:shadow-lg">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary-red to-accent-orange"></div>
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 border border-primary-light-border text-primary-red mb-6 animate-pulse">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-8 h-8"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold text-text-heading mb-3">
              Inventory Portal
            </h2>
            <p className="text-text-muted text-sm leading-relaxed">
              This page is password protected. Enter the access key below to
              decrypt and view the inventory.
            </p>
          </div>
          {!ENCRYPTED_URL ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm text-center">
              <strong>Error loading Inventory!</strong>
            </div>
          ) : (
            <form onSubmit={handleUnlock} className="space-y-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-2">
                  Access Password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    placeholder="Enter access password"
                    className="w-full p-4 border border-border-light rounded-xl outline-none focus:ring-2 focus:ring-primary-light focus:border-primary-red transition-all pr-12 text-center font-mono tracking-widest text-lg"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    disabled={decrypting}
                    autoFocus
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted">
                    🔑
                  </span>
                </div>
                {decryptError && (
                  <p className="text-primary-red text-sm mt-2 text-center font-medium animate-shake">
                    {decryptError}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={decrypting || !passwordInput}
                className="w-full bg-primary-red text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-primary-hover active:translate-y-0.5 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {decrypting ? "Decrypting..." : "Unlock Inventory"}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 bg-bg-light min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-text-heading">Product Inventory</h1>
            <p className="text-text-muted text-sm">Real-time availability and stock levels</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search by title, barcode, vendor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="p-2.5 border border-border-light rounded-lg text-sm w-full md:w-80 outline-none focus:ring-2 focus:ring-primary-red/20"
            />
            <button
              onClick={handleExport}
              className="px-4 py-2.5 bg-green-600 text-white font-semibold text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              Export
            </button>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setRetryCount((c) => c + 1)}
              className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-md hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#ef4444',
            },
          }}
        >
          <Table
            columns={columns}
            dataSource={filteredData}
            loading={loading}
            rowKey={(record) => record.barcode || record.title}
            pagination={{ pageSize: 15 }}
            scroll={{ x: 'max-content' }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};

export default Inventory;